<script setup>
/* eslint-disable no-undef */
import { defineProps } from 'vue';
import axios from 'axios';
import {ref, onMounted, computed, defineEmits} from 'vue';
import config from "@/utils/config";

const questions = ref([]);
const emit = defineEmits(['answered-questions']);

const props = defineProps({
  user: {
    type: Number,
    required: true
  },
  description: {
    type: String,
    default: ''
  },
  options: {
    type: Array,
    default: () => ["Fortemente in disaccordo", "Moderatamente in disaccordo", "Un po' in disaccordo", "Né in disaccordo né in accordo", "Un po' in accordo", "Moderatamente in accordo", "Fortemente in accordo"],
  },
  tlx: {
    type: Boolean,
    default: false
  }
}); 

onMounted(() => {
  questions.value = [
    { id: 0, text: "1. Quanto ti reputi estroverso, entusiasta?", answer: null },
    { id: 1, text: "2. Quanto ti reputi critico, litigioso?", answer: null },
    { id: 2, text: "3. Quanto ti reputi affidabile, autodisciplinato?", answer: null },
    { id: 3, text: "4. Quanto ti reputi ansioso, facilmente turbato?", answer: null },
    { id: 4, text: "5. Quanto ti reputi aperto a nuove esperienze, complesso?", answer: null },
    { id: 5, text: "6. Quanto ti reputi riservato, tranquillo?", answer: null },
    { id: 6, text: "7. Quanto ti reputi comprensivo, caloroso?", answer: null },
    { id: 7, text: "8. Quanto ti reputi disorganizzato, negligente?", answer: null },
    { id: 8, text: "9. Quanto ti reputi calmo, emotivamente stabile?", answer: null },
    { id: 9, text: "10. Quanto ti reputi convenzionale, poco creativo?", answer: null }
  ];
});

const attentionCheckPassed = computed(() => {
  const attentionCheckQuestion = questions.value.find(q => q.isAttentionCheck);
  return attentionCheckQuestion && attentionCheckQuestion.answer === attentionCheckQuestion.correctAnswer;
});

function updateAnswer(questionId, value) {
  const question = questions.value.find(q => q.id === questionId);
  if (question) {
    question.answer = value;
  }
}

const allQuestionsAnswered = computed(() => {
  return questions.value.every(question => question.answer !== null);
});

function answeredQuestions() {
  emit('answered-questions', [questions.value, attentionCheckPassed.value]);
  const tipiScores = calculateTIPIScores();

  const payload = {
    agreeableness: tipiScores.agreeableness,
    conscientiousness: tipiScores.conscientiousness,
    extraversion: tipiScores.extroversion,
    neuroticism: tipiScores.neuroticism,
    openness: tipiScores.openness,
    user: { id: tipiScores.user} 
  };

  axios.post(config.baseUrl + `/questionnaire/addScores`, payload)
    .then(() => {
      emit('complete', {payload})
    })
    .catch(error => {
      console.error('Error posting questionnaire:', error);
    });
}

const responseMapping = {
  "Fortemente in disaccordo": 1,
  "Moderatamente in disaccordo": 2,
  "Un po' in disaccordo": 3,
  "Né in disaccordo né in accordo": 4,
  "Un po' in accordo": 5,
  "Moderatamente in accordo": 6,
  "Fortemente in accordo": 7
};

function calculateTIPIScores() {
  const scores = {
    extroversion: 0,
    agreeableness: 0,
    conscientiousness: 0,
    neuroticism: 0,
    openness: 0,
    user: props.user
  };

  scores.conscientiousness = (responseMapping[questions.value[2].answer] + (8 - responseMapping[questions.value[7].answer])) / 2 * 10;
  scores.neuroticism = (responseMapping[questions.value[8].answer] + (8 - responseMapping[questions.value[3].answer])) / 2 * 10;
  scores.extroversion = (responseMapping[questions.value[0].answer] + (8 - responseMapping[questions.value[5].answer])) / 2 * 10;
  scores.agreeableness = (responseMapping[questions.value[6].answer] + (8 - responseMapping[questions.value[1].answer])) / 2 * 10;
  scores.openness = (responseMapping[questions.value[4].answer] + (8 - responseMapping[questions.value[9].answer])) / 2 * 10;
  return scores;
}

</script>

<template>
  <div class="questionnaire">
    <!-- <h3 style="margin-bottom: 20px">{{ description }}</h3> -->
    <h3 style="margin-bottom: 20px" v-html="description"></h3>
    <div class="questionnaire-content" style="display: none;">
      <slot></slot>
    </div>

    <!-- TLX Layout -->
    <div v-if="tlx" class="tlx-container">
      <div v-for="question in questions" :key="question.id" class="tlx-question">
        <div class="tlx-text">
          <div class="question-text">{{ question.text }}</div>
        </div>
        <div class="tlx-scale">
          <div class="scale-labels">
            <span>Very Low</span>
            <span>Very High</span>
          </div>
          <div class="scale-container" @click="(e) => {
              const rect = e.currentTarget.getBoundingClientRect();
              const x = e.clientX - rect.left;
              const boxWidth = rect.width / 21;
              const boxIndex = Math.floor(x / boxWidth);
              const percentage = (boxIndex / 20) * 100;
              updateAnswer(question.id, { percentage, boxIndex });
            }">
            <div class="scale-boxes">
              <div v-for="n in 21" :key="n"
                   class="box"
                   :class="{ selected: question.answer?.boxIndex === (n-1) }">
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Regular Layout -->
    <table v-else>
      <thead>
      <tr>
        <th>Question</th>
        <th v-for="option in props.options" :key="option">{{ option }}</th>
      </tr>
      </thead>
      <tbody>
      <tr v-for="question in questions" :key="question.id">
        <td class="question-td" v-html="question.text"></td>
        <!--   Old version (Unable to bold a word)  -->
        <!-- <td>{{ question.text }}</td> -->
        <td v-for="option in props.options" :key="option" @click="updateAnswer(question.id, option)">
          <input
              type="radio"
              :name="`question-${question.id}`"
              :value="option"
              :checked="question.answer === option"
          />
        </td>
      </tr>
      </tbody>
    </table>
    <button class="btn-primary"
        :disabled="!allQuestionsAnswered"
        @click="answeredQuestions"
        :title="!allQuestionsAnswered ? 'Please answer all questions before continuing': ''">Continue
    </button>
  </div>
</template>

<style scoped>
/* Existing styles */
.questionnaire{
  width: 100%;
  margin: 0 auto;
}

table {
  width: 100%;
  border-collapse: collapse;
}

th, td {
  border: 1px solid #ccc;
  padding: 8px;
  text-align: center;
}

.question-td {
  text-align: left;
}

th, tr {
  background-color: transparent;
  color: black;
}

td {
  cursor: pointer;
}

/* TLX specific styles */
.tlx-container {
  width: 100%;
  max-width: 800px;
  margin: 0 auto;
}

.tlx-question {
  margin-bottom: 2rem;
}

.tlx-text {
  margin-bottom: 0.5rem;
}

.scale-labels {
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.25rem;
  font-size: 0.9em;
  color: #666;
}

.scale-container {
  position: relative;
  height: 30px;
  cursor: pointer;
}

.scale-boxes {
  display: flex;
  height: 100%;
  gap: 1px;
}

.box {
  flex: 1;
  border: 1px solid #ccc;
  background: #f9f9f9;
  transition: background-color 0.2s ease;
}

.box.selected {
  background-color: #007bff;
  border-color: #0056b3;
}

button {
  margin-top: 20px;
  padding: 8px 16px;
  border-radius: 4px;
  border: none;
  background-color: #007bff;
  color: white;
  cursor: pointer;
}

button:disabled {
  background-color: #ccc;
  cursor: not-allowed;
}

.b {
  font-weight: bold !important;
}
</style>